import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  StreamVideo,
  StreamCall,
  LivestreamLayout,
} from "@stream-io/video-react-sdk";
import "@stream-io/video-react-sdk/dist/css/styles.css";
import { Chat, Channel, MessageList, MessageComposer } from "stream-chat-react";
import "stream-chat-react/dist/css/index.css";
import { ArrowLeft, Gift, Heart } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { fileUrl } from "../lib/api";
import { getStreamClients } from "../lib/streamClient";
import GiftLeaderboardBanner from "../components/GiftLeaderboardBanner";
import GiftBattleOverlay from "../components/GiftBattleOverlay";
import LiveChatMessage from "../components/LiveChatMessage";
import { frameStyle } from "../lib/badgeFrames";
import LiveGiftSplash from "../components/LiveGiftSplash";
import { useAuth } from "../context/AuthContext";

export default function LiveViewer() {
  const { user: me } = useAuth();
  const { sessionId } = useParams();
  const nav = useNavigate();
  const [session, setSession] = useState(null);
  const [videoClient, setVideoClient] = useState(null);
  const [chatClient, setChatClient] = useState(null);
  const [call, setCall] = useState(null);
  const [channel, setChannel] = useState(null);
  const [giftOpen, setGiftOpen] = useState(false);
  const [gifts, setGifts] = useState([]);
  const [recentGift, setRecentGift] = useState(null);
  const callRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [{ data: s }, { videoClient: v, chatClient: c }, catalog] = await Promise.all([
          api.get(`/live/${sessionId}`),
          getStreamClients(),
          api.get("/gifts/catalog"),
        ]);
        if (!mounted) return;
        setSession(s);
        setVideoClient(v);
        setChatClient(c);
        setGifts(catalog.data.gifts || []);

        const cl = v.call(s.call_type, s.call_id);
        callRef.current = cl;
        await cl.join({ create: false });
        setCall(cl);

        const ch = c.channel("livestream", s.call_id, { name: s.title });
        await ch.watch();
        setChannel(ch);
      } catch (e) {
        toast.error(e.response?.data?.detail || "This stream is no longer available");
        nav("/live");
      }
    })();
    return () => {
      mounted = false;
      if (callRef.current) callRef.current.leave().catch(() => {});
    };
  }, [sessionId, nav]);

  const sendGift = async (gift) => {
    try {
      const { data } = await api.post("/live/gift", { session_id: sessionId, gift_id: gift.id });
      setRecentGift({ ...data.gift, frame: data.sender_badge_frame });
      setTimeout(() => setRecentGift(null), 3500);
      if (channel) {
        await channel.sendMessage({
          text: `🎁 sent a ${gift.label}`,
          gift_id: gift.id,
          gift_label: gift.label,
          gift_emoji: gift.emoji,
          waves_spent: data.waves_spent,
        });
      }
      toast.success(`Sent ${gift.label} (${data.waves_spent} waves)`);
      setGiftOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send gift");
    }
  };

  if (!session || !videoClient || !call) {
    return (
      <div className="min-h-[100dvh] bg-ink flex items-center justify-center text-cream/50 text-xs tracking-[0.3em] uppercase animate-pulse">
        Tuning in…
      </div>
    );
  }

  return (
    <StreamVideo client={videoClient}>
      <StreamCall call={call}>
        <div className="h-[100dvh] bg-black relative overflow-hidden" data-testid="live-viewer">
          {/* Main livestream */}
          <div className="absolute inset-0">
            <LivestreamLayout
              showParticipantCount={true}
              showDuration={true}
              showLiveBadge={true}
            />
          </div>

          {/* Top bar */}
          <div className="absolute top-4 left-4 right-4 z-20 flex items-center justify-between">
            <button onClick={() => nav("/live")} className="w-10 h-10 rounded-full crystal-glass text-cream flex items-center justify-center" data-testid="viewer-back">
              <ArrowLeft size={18} />
            </button>
            <div className="crystal-glass px-3 py-2 rounded-full max-w-[60%] truncate">
              <span className="text-cream text-xs font-bold truncate block">{session.title}</span>
              <span className="text-cream/60 text-[10px] tracking-[0.2em] uppercase">@{session.host_username}</span>
            </div>
          </div>

          {/* Gift battle + leaderboard */}
          <div className="absolute top-[72px] left-4 right-4 z-20 space-y-2">
            <GiftBattleOverlay sessionId={sessionId} isHost={false} />
            <GiftLeaderboardBanner sessionId={sessionId} />
          </div>
          <LiveGiftSplash sessionId={sessionId} isAdmin={me?.role === "admin"} />

          {/* Floating gift toast */}
          {recentGift && (
            <div className="absolute top-1/3 left-1/2 -translate-x-1/2 z-30 animate-fade-up pointer-events-none">
              <div className={`crystal-glass px-6 py-4 rounded-3xl text-center border ${frameStyle(recentGift.frame) ? `${frameStyle(recentGift.frame).border} ${frameStyle(recentGift.frame).glow}` : "border-transparent"}`} data-testid="viewer-gift-toast">
                {recentGift.image_url ? (
                  <img src={fileUrl(recentGift.image_url)} alt="" className="w-16 h-16 object-contain rounded-2xl mx-auto mb-2" />
                ) : (
                  <div className="text-5xl">{recentGift.emoji}</div>
                )}
                <p className={`text-sm font-black mt-1 ${frameStyle(recentGift.frame) ? "text-jam-gold" : "text-papaya"}`}>{recentGift.label}</p>
                {recentGift.designer_username && <p className="text-sky-200 text-[10px] font-black tracking-[0.15em] uppercase mt-1" data-testid="viewer-gift-designer">✦ designed by @{recentGift.designer_username}</p>}
              </div>
            </div>
          )}

          {/* Chat panel — bottom half */}
          {chatClient && channel && (
            <div className="absolute bottom-[88px] left-0 right-0 max-h-[40vh] z-20 pointer-events-none">
              <div className="pointer-events-auto bg-ink/40 backdrop-blur-md border-t border-white/10 max-h-[40vh] flex flex-col">
                <Chat client={chatClient} theme="str-chat__theme-dark">
                  <Channel channel={channel}>
                    <div className="h-[35vh] flex flex-col">
                      <div className="flex-1 overflow-hidden">
                        <MessageList Message={LiveChatMessage} />
                      </div>
                      <MessageComposer />
                    </div>
                  </Channel>
                </Chat>
              </div>
            </div>
          )}

          {/* Bottom action bar */}
          <div className="absolute bottom-0 left-0 right-0 p-4 z-30 bg-gradient-to-t from-ink to-transparent flex items-center justify-end gap-3">
            <button
              onClick={() => setGiftOpen(true)}
              data-testid="viewer-gift-open"
              className="w-14 h-14 rounded-full bg-papaya text-ink flex items-center justify-center papaya-glow"
              title="Send a gift"
            >
              <Gift size={26} weight="fill" />
            </button>
          </div>

          {/* Gift picker sheet */}
          {giftOpen && (
            <div className="absolute inset-0 z-40 bg-ink/80 backdrop-blur-sm flex items-end" onClick={() => setGiftOpen(false)}>
              <div className="w-full bg-ink rounded-t-3xl p-5 max-h-[70vh] overflow-y-auto border-t border-white/10" onClick={(e) => e.stopPropagation()} data-testid="viewer-gift-panel">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-cream text-2xl font-black tracking-tighter">Send a gift</h3>
                  <button onClick={() => setGiftOpen(false)} className="text-cream/60 text-xs tracking-[0.2em] uppercase">Close</button>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {gifts.map((g) => (
                    <button
                      key={g.id}
                      onClick={() => sendGift(g)}
                      data-testid={`viewer-gift-${g.id}`}
                      className="rounded-2xl bg-white/5 border border-white/10 hover:border-papaya p-3 flex flex-col items-center transition-colors"
                    >
                      {g.is_community && (
                        <span className={`text-[8px] font-black tracking-widest uppercase px-1.5 py-0.5 rounded-full mb-1 ${g.is_limited ? "bg-jam-gold text-jam-black" : "bg-emerald-400 text-ink"}`}>{g.badge || (g.is_limited ? "Limited" : "Community")}</span>
                      )}
                      {g.image_url ? (
                        <img src={fileUrl(g.image_url)} alt="" className="w-12 h-12 object-contain rounded-xl" style={g.custom ? { background: `linear-gradient(135deg, ${g.custom.colors?.bg1}, ${g.custom.colors?.bg2})` } : undefined} />
                      ) : (
                        <div className="text-3xl">{g.emoji}</div>
                      )}
                      <p className="text-cream text-[11px] font-bold mt-2 text-center leading-tight">{g.label}</p>
                      <p className="text-papaya text-[10px] tracking-[0.15em] uppercase font-black mt-1">{g.waves} W</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </StreamCall>
    </StreamVideo>
  );
}
